import React, { useState } from 'react'
import { useRouter } from 'next/router'
import SiteLayout from 'components/SiteLayout'
import Head from 'components/Head'
import { FormsStore } from 'stores/forms'
import { usePageView, useStore } from 'hooks'
import { THEME, getStaticWrapper } from 'utils'
import * as Yup from 'yup'
import { Formik, Form, Field } from 'formik'
import { sendEvent } from 'lib/ga'
import segment from 'lib/segment'

const schema = Yup.object({
  email: Yup.string().required('Field is required').min(1, 'Field is required').email('Must be a valid email')
})

const NewsletterPage = () => {
  const [error, setError] = useState(undefined)
  const formsStore = useStore(FormsStore)
  const router = useRouter()
  usePageView()

  async function handleSubmit({ email }) {
    try {
      await formsStore.postSubmission(process.env.RANDOM_THOUGHTS_FORM_ID, {
        email,
        fields: [{ prompt: 'email', answer: email }]
      })
      sendEvent('newsletter signup', 'random thoughts', { label: document?.referrer ?? '', value: 1 })
      segment.track('Newsletter Subscribed', { newsletter: 'randomthoughts', email })
      const base64Email = window.btoa(email)
      router.push(`/newsletter/more-newsletters?id=${base64Email}`)
    } catch (err) {
      setError(err.message || 'There was an error with submitting')
      console.error(err)
    }
  }

  return (
    <SiteLayout>
      <Head
        title='Barstool Sports Newsletter'
        description='Get the best of Barstool Sports in your inbox, five days a week'
      />
      <section className='newsletterForm'>
        <section className='newsletterPageHeader'>
          <img src='/static/images/randomThoughts.svg' alt='Random Thoughts Newsletter' />
        </section>
        <div className='klaviyo-container-newsletter'>
          <p className='newsletterDescription'>Get the best of Barstool Sports in your inbox, five days a week.</p>
          <Formik
            initialValues={{ email: '' }}
            onSubmit={handleSubmit}
            validationSchema={schema}
            validateOnChange={false}
            validateOnBlur={true}
            validateOnMount={false}
          >
            <Form>
              <div className='email-submit-box'>
                <Field name='email'>
                  {({ field, meta }) => (
                    <>
                      <input
                        className={`email-input ${meta.touched && meta.error ? 'errored' : ''}`}
                        placeholder='Email Address'
                        type='email'
                        {...field}
                      />
                      {meta.touched && meta.error && <div className='field-error'>{meta.error}</div>}
                      <button className='email-submit' type='submit'>
                        Sign Up
                      </button>
                    </>
                  )}
                </Field>
                <p className='submit-legal'>
                  <span>By submitting this form I agree to the Barstool Sports, Inc. </span>
                  <a href='/terms-of-use' target='_blank' rel='norefferer'>
                    Terms of Use
                  </a>
                  <span> and </span>
                  <a href='/privacy-policy' target='_blank' rel='norefferer'>
                    Privacy Policy
                  </a>
                  .
                </p>
                {error && (
                  <div className='error-box'>
                    <p className='form__error'>{error || 'Please complete all fields above'}</p>
                  </div>
                )}
              </div>
            </Form>
          </Formik>
        </div>
      </section>
      <style jsx>{`
        .newsletterForm {
          margin-top: 1rem;
          box-shadow: ${THEME.BOX_SHADOW.DEFAULT};
          background: #fff;
          position: relative;
          max-width: 26.5rem;
          padding: 1rem 0;
        }
        .newsletterPageHeader {
          padding: 1rem;
          text-align: center;
        }
        .newsletterPageHeader img {
          max-width: 10rem;
          height: auto;
        }
        .klaviyo-container-newsletter {
          max-width: 400px;
          padding: 10px 5px;
          margin: auto;
        }
        .newsletterDescription {
          font-size: 20px;
          font-weight: 700;
          text-align: center;
          padding: 10px 6px;
        }
        .email-input {
          border-radius: 2px;
          padding: 0px 0px 0px 16px;
          height: 52px;
          color: #000;
          font-size: 16px;
          background-color: #fff;
          border: 1px solid rgb(180, 187, 195);
        }
        .email-input:hover {
          border-color: #000;
        }
        .email-input.errored {
          border-color: #d0331f;
        }
        .field-error {
          color: #d0331f;
          font-size: 12px;
          margin: -5px 0 10px;
        }
        .submit-legal {
          font-size: 10px;
          text-align: center;
          color: #666;
          max-width: 225px;
          margin: auto;
          line-height: 1.2;
        }
        .submit-legal a {
          text-decoration: underline;
          color: #666;
        }
        @media (min-width: 47.9375rem) {
          .newsletterForm {
            margin: 3rem auto;
          }
        }
      `}</style>
    </SiteLayout>
  )
}

export const getStaticProps = getStaticWrapper()

export default NewsletterPage
